import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import type { ChatMessage, ChatResponse } from '../../api/chat';

import { getErrorMessage } from './error-handling';

/**
 * Guide flow utilities for BhanzuBuddy chat
 *
 * Handles the guide me functionality and related API interactions.
 */

export interface UseGuideFlowProps {
  leadId: string;
  selectedVariantId?: string;
  currentSessionUuid: string;
  guideMutation: ReturnType<typeof import('@tanstack/react-query').useMutation>;
  onMessageSent?: (response: ChatResponse) => void;
  onError?: (error: unknown) => void;
}

export const useGuideFlow = ({
  leadId,
  selectedVariantId,
  currentSessionUuid,
  guideMutation,
  onMessageSent,
  onError,
}: UseGuideFlowProps) => {
  const queryClient = useQueryClient();

  // Custom success handler for guide flow that doesn't invalidate chat history
  const handleGuideSuccess = React.useCallback(
    (data: ChatResponse) => {
      onMessageSent?.(data);
      return data;
    },
    [onMessageSent]
  );

  // Custom error handler for guide flow
  const handleGuideError = React.useCallback(
    (error: unknown) => {
      onError?.(error);
      return getErrorMessage(error);
    },
    [onError]
  );

  // Handle adding messages and sending API call for guide flow
  const processGuideMessages = React.useCallback(
    (question: string) => {
      // Step 3: Send to answer-all-questions API with custom success handling
      const chatData: ChatMessage = {
        question,
        lead_id: leadId,
        source: 'guide_me',
        variant_id: selectedVariantId ?? '',
        session_uuid: currentSessionUuid || undefined,
      };

      guideMutation.mutate(chatData, {
        onSuccess: handleGuideSuccess,
        onError: handleGuideError,
      });
    },
    [
      leadId,
      selectedVariantId,
      currentSessionUuid,
      guideMutation,
      handleGuideSuccess,
      handleGuideError,
    ]
  );

  // Handle guide me flow with proper API sequence
  const handleGuideFlow = React.useCallback(
    async (question: string) => {
      const queryKey = [
        'bhanzu-buddy',
        'chat-history',
        'get',
        { lead_id: leadId, source: 'guide_me' },
      ] as const;
      // Step 1: Fetch interaction-history for guide_me and allow UI to seed from it
      await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const mod = await import('../../api/chat-history');
          return mod.getChatHistory({ lead_id: leadId, source: 'guide_me' });
        },
      });
      // Step 2: Then call answer_all_questions
      processGuideMessages(question);
    },
    [leadId, queryClient, processGuideMessages]
  );

  return {
    handleGuideFlow,
    processGuideMessages,
    handleGuideSuccess,
    handleGuideError,
  };
};
